import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAPI } from '../../API';
import { ActionTypes, useAppDispatch, useAppState } from '../../Store';
import { useTranslation } from '../../Translation';
import SidebarNav from '../../Components/SidebarNav/SidebarNav';
import useAuthenticated from '../Dashboard/useAuthenticated';
import Table from '../../Components/Table/Table';
import LoadingCircleTransparent from '../../Components/LoadingIndicators/LoadingCircleTransparent';
import { usePaginations } from '../../Hooks/usePaginations';
import { useDateFormat } from '../../Hooks/useDateFormat';
import Modal from '../../Components/Modal';
import { useRefresh } from '../../Hooks/useRefresh';
import { TextInput } from '../../Components/Inputs/TextInput';
import { Button } from '../../Components/Buttons';
import { DocumentDuplicateIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import Checkbox from '../../Components/Inputs/Checkbox';
import { AutomatisationFilterModal, AutomatisationModal, AutomatisationName, useAutomatisationMappingForTable } from './Automatisations';


export default function Campaigns() {

    const { translate, language, setLanguage } = useTranslation();

    const { isAuthenticated, pathAfterLogin } = useAuthenticated();

    const [open, setOpen] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [filterOpen, setFilterOpen] = useState(false);
    const api = useAPI();
    const appState = useAppState();
    const { sidebarSearch } = appState;

    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const pagination = usePaginations();
    const { refreshId, refresh } = useRefresh();
    const [tags, setTags] = useState([]);
    const [tagsForFilter, setInternalTagsForFilter] = useState(localStorage.getItem('campaigns_tags_filter') ? JSON.parse(localStorage.getItem('campaigns_tags_filter')) : []);

    const setTagsForFilter = (tags) => {
        setInternalTagsForFilter(tags);
        localStorage.setItem('campaigns_tags_filter', JSON.stringify(tags));
    }

    useEffect(() => {
        
        api.listTags(1, 99999).then((response) => {
            setTags(response.data.tags);
        });
        
    }, [open]);

    useEffect(() => {
        pagination.setPage(1);
    }, [sidebarSearch]);

    const handleSort = ({ key }) => {
        if (pagination.sortBy === key) {
            pagination.setSortOrder(pagination.sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            pagination.setSortBy(key);
            pagination.setSortOrder('asc');
        }
        pagination.setPage(1);
    }

    useEffect(() => {
        const fetchAutomatisations = async () => {
            try {
                setLoading(true);
                const response = await api.listPaginatedCampaigns(pagination.page, pagination.pageSize, tagsForFilter.map((tag) => tag.id), sidebarSearch, pagination.sortBy, pagination.sortOrder);
                setCampaigns(response.data.automatisations);
                pagination.setMetaData(response.data.meta);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        }

        fetchAutomatisations();
    }, [pagination.page, pagination.sortBy, pagination.sortOrder, refreshId, sidebarSearch, tagsForFilter]);

    const handleDelete = async (campaign) => {
        try {
            await api.deleteAutomatisation(campaign.id);
            refresh();
        } catch (error) {
            console.error(error);
        }
    }

    const handleCopy = async (campaign) => {
        try {
            await api.copyAutomatisation(campaign.id);
            refresh();
        } catch (error) {
            console.error(error);
        }
    }

    const mappedCampaigns = useAutomatisationMappingForTable(campaigns);
    return (
        <>
            {/* Place the Modal outside the main container */}
            <Modal
                open={open}
                onClose={() => setOpen(false)}
                title={selectedCampaign ? translate('automatisations.edit') : translate('automatisations.create')}
                description={''}
                children={<AutomatisationModal tags={tags} refresh={refresh} setOpen={setOpen} automatisation={selectedCampaign} setSelectedAutomatisation={setSelectedCampaign} open={open} isCampaign={true} />}
            />

            <Modal
                open={filterOpen}
                onClose={() => setFilterOpen(false)}
                title={translate('campaigns.filter')}
                description={''}
                children={<AutomatisationFilterModal setTagsForFilter={setTagsForFilter} tagsForFilter={tagsForFilter} tags={tags} refresh={refresh} setOpen={setFilterOpen} open={filterOpen} />}
            />

            <div className="flex min-h-full w-full">
                <SidebarNav currentPage="campaigns">
                    {loading ? <div className="flex justify-center items-center h-full w-12">
                    <LoadingCircleTransparent />
                </div> : (
                    <Table
                        title={translate('campaigns.title')}
                        description={translate('campaigns.description')}
                        data={mappedCampaigns}
                        translationKey="campaigns.table"
                        columns={[{key: 'name', type: 'custom', render: (row) => <AutomatisationName row={row} />}, 'created_at']}
                        pagination={pagination}
                        onSort={handleSort}
                        onRowClick={(row) => {
                            navigate(`/campaign/${row.id}`);
                        }}
                        createButton={{
                            text: translate('campaigns.create'),
                            action: () => {
                                setSelectedCampaign(null);
                                setOpen(true);
                            }
                        }}
                        filterButton={{
                            text: `${translate('filter')} (${tagsForFilter.length})`,
                            action: () => {
                                setFilterOpen(true);
                            }
                        }}
                        dropdownGroups={[{
                            options: [{
                                label: translate('edit'),
                                icon: <PencilIcon aria-hidden="true" className="mr-3 size-5 text-gray-400 group-data-[focus]:text-gray-500" />,
                                action: (row) => { setSelectedCampaign(row); setOpen(true) }
                            }
                            ]
                        }, {
                            options: [{
                                label: translate('clone'),
                                icon: <DocumentDuplicateIcon aria-hidden="true" className="mr-3 size-5 text-gray-400 group-data-[focus]:text-gray-500" />,
                                action: (row) => handleCopy(row)
                            }]
                        }, {
                            options: [{
                                label: translate('delete'),
                                icon: <TrashIcon aria-hidden="true" className="mr-3 size-5 text-red-400 group-data-[focus]:text-gray-500" />,
                                action: (row) => handleDelete(row)
                            }]
                        }]}
                    />
                )}
            </SidebarNav>
        </div>
        </>

    );
}